<template>
  <div class="portfolio-holdings-area">
    <div v-if="!isDataLoaded" class="loading-state">
      <p>Loading portfolio data...</p>
    </div>
    
    <template v-else>
      
      <div class="holdings-table">
        <table v-if="tableData.length > 0">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Name</th>
              <!-- <th>Market Value</th> -->
              <th>% of Portfolio</th>
              <th>Expense Ratio</th>
              <th>Security Type</th>
              <th>Account</th>
              <th>Account Registration</th>
              <th>Account Tax Status</th>
              <th>Matched</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableData" :key="index" :class="{ 'selected': selectedRow === index }" @click="selectedRow = index">
              <td>{{ row.activeSymbol }}</td>
              <td>{{ row.name }}</td>
              <!-- <td>{{ toCurrency(row.marketValue) }}</td> -->
              <td>{{ toPercent(row.pctPortfolio) }}</td>
              <td>{{ toPercent(row.expenseRatio) }}</td>
              <td>{{ row.securityType }}</td>
              <td>{{ row.account }}</td>
              <td>{{ row.accountRegistration }}</td>
              <td>{{ row.accountTaxStatus }}</td>
              <td>{{ row.matched }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="no-data">
          No holdings data available for this portfolio.
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js'; //need this for custom holdings format

import PortfolioSummaryTree from '@frontend/ui/features/checkup/components/PortfolioSummaryTree.vue';

import { isPlainObject } from '@frontend/shared/Util.js';

export default {
  name: 'PortfolioHoldingsArea',
  components: {
    PortfolioSummaryTree,
  },
  props: {
    portfolioType: {
      type: String,
      required: true,
      validator: function(value) {
        // The value must match one of these strings
        return ['portfolio', 'benchmark', 'optimizedPortfolio'].includes(value);
      }
    }
  },
  data() {
    return {
      summaryTree: {},
      selectedRow: null,
      isDataLoaded: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    tableData() {
      const portfolio = this.checkupStore[this.portfolioType];
      if (!portfolio || !portfolio.holdings || !Array.isArray(portfolio.holdings)) {
        return [];
      }
      
      // Safely calculate total portfolio value
      const totalPortfolioValue = portfolio.holdings.reduce(
        (total, holding) => total + (typeof holding.dollars === 'number' ? holding.dollars : 0),
        0
      );
      
      return portfolio.holdings.map(holding => {
        // Default values in case some properties are missing
        const dollars = typeof holding.dollars === 'number' ? holding.dollars : 0;
        
        return {
          activeSymbol: this.getTickerField(holding, 'symbol'),
          name: this.getName(holding),
          marketValue: dollars,
          pctPortfolio: totalPortfolioValue > 0 ? dollars / totalPortfolioValue : 0,
          expenseRatio: this.getTickerField(holding, 'expense_ratio'),
          securityType: this.getSecurityType(holding),
          account: this.getAcct(holding),
          accountRegistration: this.getAcctReg(holding),
          accountTaxStatus: this.getAcctTaxStatus(holding),
          matched: holding.found === false ? '❌' : '✅',
        };
      });
    },
  },
  async mounted() {
    await this.fetchHoldings();
    
    // Check if we have data after fetching
    const portfolio = this.checkupStore[this.portfolioType];
    if (portfolio?.holdings) {
      this.isDataLoaded = true;
    }
  },
  methods: {
    async fetchHoldings() {
      if ((this.portfolioType === 'benchmark' || this.portfolioType === 'optimizedPortfolio') && !this.checkupStore[this.portfolioType]?.holdings?.length) {
        let portfolio_external_id = this.checkupStore[this.portfolioType]?.external_id; 
        if (this.checkupStore[this.portfolioType]?.id === 'default_benchmark') {
          portfolio_external_id = 'default_benchmark';
        }
        if (portfolio_external_id) {
          // Get holdings data from API
          const result = await this.checkupStore.getPortfolioHoldings(portfolio_external_id);
          
          // Verify the data is in the expected format
          if (Array.isArray(result) && result.length === 2) {
            const [holdings, uploaded_df_agg] = result;
            
            // Make sure holdings is an array
            if (Array.isArray(holdings)) {
              if (isPlainObject(this.checkupStore[this.portfolioType])) {
                this.checkupStore[this.portfolioType].holdings = holdings;
                this.checkupStore[this.portfolioType].uploaded_df_agg = uploaded_df_agg;
              }
            } else {
              console.error('Holdings data is not in expected format:', holdings);
            }
          } else {
            console.error('API response format unexpected:', result);
          }
        }
      }
    },
    getUploadedSymbol(holding) {
      if (typeof holding.ticker === 'string' && holding.found === false) {
        return holding.ticker;
      } else {
        try {
          if (holding.metadata?.uploadedsymbol === holding.ticker.symbol && holding.metadata?.notes) {
            return holding.metadata.uploadedname;
          }
          return holding.metadata?.uploadedsymbol;
        } catch (error) {
          return holding.ticker || holding.isin || holding.cusip || '';
        }
      }
    },
    getName(holding) {
      if (holding.uploadedname) {
        return holding.uploadedname;
      }
      if (holding.metadata?.uploadedname && holding.metadata?.uploadedsymbol !== holding.ticker.symbol) {
        return holding.metadata.uploadedname;
      }
      return this.getTickerField(holding, 'full_name');
    },
    getSecurityType(holding) {
      if (holding.securitytype) return holding.securitytype;
      return this.getTickerField(holding, 'asset_type');
    },
    getAcct(holding) {
      if (holding.account) {
        return holding.account;
      }
      return null;
    },
    getAcctTaxStatus(holding) {
      const acct_tax_status =  this.getPortfolioField(holding,'account_tax_status_string')
      if (acct_tax_status) {
        return acct_tax_status
      }
      return holding.account_tax_status;
    },
    getAcctReg(holding) {
      if (holding.account_registration) {
        return holding.account_registration;
      }
      if (holding.accountregistration){
        return holding.accountregistration
      }
      return null;
    },
    getTickerField(holding, field) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        return holding.ticker[field];
      }
      return null;
    },
    getPortfolioField(holding, field) {
      if (typeof holding === 'object' && holding.ticker !== null) {
        return holding[field];
      }
      return null;
    },
    toCurrency(value) {
      if (typeof value === 'string') {
        if (value.startsWith('$')) return value;
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }

      const dollars = value?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return dollars ? `$${dollars}` : '';
    },
    toPercent(value) {
      if (typeof value === 'string') {
        if (value.endsWith('%')) return value;
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }

      const percent = value?.toLocaleString('en-US', {
        style: 'percent',
        maximumFractionDigits: 2,
      });
      return percent;
    },
  },
  watch: {
    portfolioType() {
      this.isDataLoaded = false;
      this.fetchHoldings().then(() => {
        this.isDataLoaded = true;
      });
    },
    'checkupStore.portfolio': {
      deep: true,
      handler(newVal) {
        if (this.portfolioType === 'portfolio' && newVal?.holdings) {
          this.isDataLoaded = true;
        }
      }
    },
    'checkupStore.benchmark': {
      deep: true,
      handler(newVal) {
        if (this.portfolioType === 'benchmark' && newVal?.holdings) {
          this.isDataLoaded = true;
        }
      }
    },
    'checkupStore.optimizedPortfolio': {
      deep: true,
      handler(newVal) {
        if (this.portfolioType === 'optimizedPortfolio' && newVal?.holdings) {
          this.isDataLoaded = true;
        }
      }
    }
  }
};
</script>

<style scoped>
.portfolio-holdings-area {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.holdings-table {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  color: black; /* Ensure text is black */
}

th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.selected {
  background-color: #f0f7ff;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-style: italic;
  color: #666;
}
</style>